import { motion } from "framer-motion";
import Carousel from "./Carousel";
import { isPresent, hasItems } from "./helpers";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { useEffect, useState } from "react";

function StarRating({ rating }) {
  return (
    <div className="flex gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          className={`w-4 h-4 ${
            i < rating
              ? "text-[var(--dvx-primary)]"
              : "text-[var(--dvx-border)]"
          }`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function TestimonialCard({ item }) {
  return (
    <div className="bg-[var(--dvx-bg)] border border-[var(--dvx-border)] p-6 h-full">
      {isPresent(item.rating) && <StarRating rating={item.rating} />}

      <p className="text-[var(--dvx-text-muted)] font-manrope text-sm leading-relaxed mb-6">
        "{item.text}"
      </p>

      <div className="flex items-center gap-3">
        {isPresent(item.avatar) && (
          <img
            src={item.avatar}
            alt={item.name}
            className="w-10 h-10 rounded-full object-cover"
          />
        )}

        <div>
          <div className="font-oswald text-[var(--dvx-text)] font-bold text-sm uppercase">
            {item.name}
          </div>

          {isPresent(item.program) && (
            <div className="text-[var(--dvx-primary)] text-xs">
              {item.program}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QualificationCTA() {
  return (
    <div className="bg-[var(--dvx-bg)] border border-[var(--dvx-border)] p-8 shadow-sm transition-colors duration-300">
      <h3 className="font-oswald text-lg font-semibold text-[var(--dvx-text)] mb-6">
        Quick qualification
      </h3>

      <ul className="space-y-4 text-sm text-[var(--dvx-text-muted)]">
        <li className="flex items-center gap-3">
          <Check
            size={16}
            className="text-[var(--dvx-primary)] mt-0.5 flex-shrink-0"
          />
          You're serious about consistency
        </li>

        <li className="flex items-center gap-3">
          <Check
            size={16}
            className="text-[var(--dvx-primary)] mt-0.5 flex-shrink-0"
          />
          You want a premium system
        </li>

        <li className="flex items-center gap-3">
          <Check
            size={16}
            className="text-[var(--dvx-primary)] mt-0.5 flex-shrink-0"
          />
          You want results within a timeline
        </li>

        <li className="flex items-center gap-3">
          <Check
            size={16}
            className="text-[var(--dvx-primary)] mt-0.5 flex-shrink-0"
          />
          You follow structure
        </li>
      </ul>

      <Button
        onClick={() => {
          window.location.href = "/contact";
        }}
        className="w-full mt-8 py-6 rounded-none font-oswald uppercase tracking-widest text-sm bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white hover:scale-105 transition-transform"
      >
        Apply now
        <ArrowRight className="ml-2" size={16} />
      </Button>
      <p className="text-xs text-[var(--dvx-text-muted)] mt-4">
        If you qualify, you'll be contacted for a strategy call.
      </p>
    </div>
  );
}

export default function TestimonialsPreview({ section, testimonials }) {
  const [itemsPerPage, setItemsPerPage] = useState(2);

  useEffect(() => {
    const updateItems = () => {
      if (window.innerWidth < 768) {
        setItemsPerPage(1); // mobile
      } else if (window.innerWidth < 1024) {
        setItemsPerPage(2); // tablet
      } else {
        setItemsPerPage(2); // desktop
      }
    };
    updateItems();
    window.addEventListener("resize", updateItems);
    return () => window.removeEventListener("resize", updateItems);
  }, []);

  if (!section) return null;

  const items = (testimonials || []).filter(
    (t) => isPresent(t.text) && isPresent(t.name),
  );

  if (!hasItems(items)) return null;

  return (
    <section
      id="testimonials"
      className="py-24 md:py-32 bg-[var(--dvx-surface)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* TITLE */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          {isPresent(section.badge) && (
            <h3 className="text-[var(--dvx-primary)] font-oswald text-xl uppercase tracking-[0.2em]">
              {section.badge}
            </h3>
          )}

          {isPresent(section.title) && (
            <h2 className="mt-4 font-oswald text-4xl font-bold text-[var(--dvx-text)] uppercase">
              {section.title}
            </h2>
          )}
        </motion.div>

        {/* MAIN GRID */}
        <div className="grid lg:grid-cols-4 gap-10">
          {/* REVIEWS 75% */}
          <div className="lg:col-span-3 overflow-hidden">
            <Carousel
            items={items}
            itemsPerPage={itemsPerPage}
            autoplayMs={5000}
            pauseOnHover={true}
            renderItem={(item) => <TestimonialCard item={item} />}
          />
          </div>

          {/* CTA 25% */}
          <div className="lg:col-span-1 mt-6 lg:mt-0">
            <QualificationCTA />
          </div>
        </div>
      </div>
    </section>
  );
}
